import { useCallback, useSyncExternalStore } from "react"

/**
 * App-wide notification sound: a short synthesized "ding" reused by any feature
 * that wants an audible cue (the WhatsApp inbox, the global unseen-notification
 * snackbar, …). A single persisted mute governs them all.
 *
 * A module-level singleton (not React state) because the audio context and its
 * unlock listener must exist exactly once regardless of which component mounts
 * first. Best-effort by design: browsers block audio until the user has
 * interacted with the page (autoplay policy), and a muted iOS device plays no
 * web audio at all — callers should pair it with a visual cue.
 */

const MUTED_STORAGE_KEY = "notifications:sound-muted"
// Minimum gap between dings so a burst of events doesn't machine-gun.
const DING_THROTTLE_MS = 3000

let audioContext: AudioContext | null = null
let listenersAttached = false
let lastDingAt = 0

function getAudioContext(): AudioContext | null {
	if (audioContext) {
		return audioContext
	}
	const Ctor =
		window.AudioContext ||
		(window as unknown as { webkitAudioContext?: typeof AudioContext })
			.webkitAudioContext
	if (!Ctor) {
		return null
	}
	audioContext = new Ctor()
	return audioContext
}

/**
 * Attach the one-time listeners that "unlock" audio on the first user gesture.
 * Idempotent — safe to call from every render that might play a sound.
 */
export function initNotificationSound() {
	if (listenersAttached) {
		return
	}
	listenersAttached = true

	const unlock = () => {
		const ctx = getAudioContext()
		// Resuming inside the gesture is what satisfies the autoplay policy; from
		// here on, event-driven playback is allowed.
		ctx?.resume().catch(() => undefined)
		window.removeEventListener("pointerdown", unlock)
		window.removeEventListener("keydown", unlock)
	}
	window.addEventListener("pointerdown", unlock)
	window.addEventListener("keydown", unlock)
}

// Emit the actual tone — a soft two-step blip, a brief rise then quick decay.
function emitTone(ctx: AudioContext) {
	const now = ctx.currentTime
	const osc = ctx.createOscillator()
	const gain = ctx.createGain()
	osc.type = "sine"
	osc.frequency.setValueAtTime(660, now)
	osc.frequency.setValueAtTime(880, now + 0.08)
	gain.gain.setValueAtTime(0.0001, now)
	gain.gain.exponentialRampToValueAtTime(0.2, now + 0.02)
	gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28)
	osc.connect(gain).connect(ctx.destination)
	osc.start(now)
	osc.stop(now + 0.3)
}

/**
 * Play the ding if not muted, throttled so bursts don't machine-gun. Passive:
 * if the context isn't unlocked yet (no gesture), this ding is skipped.
 */
export function playNotificationSound() {
	if (isNotificationSoundMuted()) {
		return
	}
	const now = Date.now()
	if (now - lastDingAt < DING_THROTTLE_MS) {
		return
	}
	lastDingAt = now
	const ctx = getAudioContext()
	if (!ctx) {
		return
	}
	if (ctx.state === "suspended") {
		ctx.resume().catch(() => undefined)
		return
	}
	emitTone(ctx)
}

/**
 * Play the ding immediately as a confirmation (e.g. when the user unmutes),
 * ignoring mute and throttle. Always runs inside a user gesture, so it resumes
 * the context first and then plays — reliable even on the very first click.
 */
export function playTestDing() {
	const ctx = getAudioContext()
	if (!ctx) {
		return
	}
	if (ctx.state === "suspended") {
		ctx
			.resume()
			.then(() => emitTone(ctx))
			.catch(() => undefined)
		return
	}
	emitTone(ctx)
}

// --- Mute state (persisted, reactive) --------------------------------------

const muteListeners = new Set<() => void>()

function readMuted(): boolean {
	try {
		return window.localStorage.getItem(MUTED_STORAGE_KEY) === "1"
	} catch {
		return false
	}
}

let mutedSnapshot = readMuted()

export function isNotificationSoundMuted(): boolean {
	return mutedSnapshot
}

export function setNotificationSoundMuted(next: boolean) {
	mutedSnapshot = next
	try {
		window.localStorage.setItem(MUTED_STORAGE_KEY, next ? "1" : "0")
	} catch {
		// Ignore — a rejected localStorage write just means the choice isn't
		// remembered across reloads.
	}
	muteListeners.forEach((listener) => listener())
}

/**
 * Reactive mute state + a stable toggle. Also (idempotently) primes audio, so
 * mounting this anywhere is enough. Unmuting plays a confirmation ding.
 */
export function useNotificationSoundMute() {
	const muted = useSyncExternalStore(
		(listener) => {
			muteListeners.add(listener)
			return () => muteListeners.delete(listener)
		},
		isNotificationSoundMuted,
		isNotificationSoundMuted
	)
	const toggle = useCallback(() => {
		const next = !isNotificationSoundMuted()
		setNotificationSoundMuted(next)
		// Just turned sound on — play the ding so the user hears it works (and the
		// click primes the audio context).
		if (!next) {
			playTestDing()
		}
	}, [])
	return { muted, toggle }
}
